import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI } from './api';

const AuthContext = createContext(null);

// Helper function to build roles array from user data
const buildUserRoles = (userData) => {
    if (!userData) return [];
    
    const roles = [];
    const userType = userData.user_type;
    
    // Add primary role
    if (userType) {
        roles.push(userType);
    }
    
    // If user is also a tutor (upgraded from tutor to provider)
    if (userData.is_also_tutor || userData.original_user_type === 'tutor') {
        if (!roles.includes('tutor')) {
            roles.push('tutor');
        }
    }
    
    // If primary is tutor, check if they're also a provider
    if (userType === 'tutor' && userData.provider_type) {
        roles.push(userData.provider_type);
    }
    
    return roles;
};

// Enhance user data with roles
const enhanceUserData = (userData) => {
    if (!userData) return null;
    return {
        ...userData,
        roles: buildUserRoles(userData)
    };
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const tokenRef = useRef(null);
    const [token, setToken] = useState(() => {
        try {
            const storedToken = localStorage.getItem('receppet_token');
            tokenRef.current = storedToken;
            return storedToken;
        } catch (e) {
            console.warn('localStorage not available');
            return null;
        }
    });

    const loadUser = useCallback(async () => {
        const currentToken = tokenRef.current || token;
        
        // Se não há token, não há sessão
        if (!currentToken && !(typeof document !== 'undefined' && document.cookie.includes('session_token'))) {
            setUser(null);
            setLoading(false);
            return;
        }

        // SEMPRE buscar dados frescos da API - NUNCA confiar no localStorage para dados do usuário
        try {
            const response = await authAPI.getMe();
            const freshUser = enhanceUserData(response.data);
            
            
            setUser(freshUser);
            try {
                localStorage.setItem('receppet_user', JSON.stringify(freshUser));
            } catch (e) {
                console.warn('Could not save to localStorage');
            }
        } catch (error) {
            console.error('Error loading user:', error);
            // Se erro de autenticação, limpar sessão
            if (error.response?.status === 401) {
                try {
                    localStorage.removeItem('receppet_token');
                    localStorage.removeItem('receppet_user');
                } catch (e) {}
                tokenRef.current = null;
                setToken(null);
                setUser(null);
            }
        }
        setLoading(false);
    }, [token]);

    useEffect(() => {
        loadUser();
    }, [loadUser]);

    const login = async (email, password) => {
        // CRÍTICO: Limpar TODA sessão anterior antes de fazer login
        try {
            localStorage.removeItem('receppet_token');
            localStorage.removeItem('receppet_user');
            localStorage.removeItem('receppet_active_panel');
            // Limpar qualquer outro dado que possa interferir
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith('receppet_')) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => localStorage.removeItem(key));
        } catch (e) {
            console.warn('Could not clear localStorage');
        }
        // Limpar cookies de sessão para evitar leak entre logins
        try {
            document.cookie = 'session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            document.cookie = 'session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=' + window.location.hostname;
        } catch (e) {}
        tokenRef.current = null;
        setToken(null);
        setUser(null);
        
        const response = await authAPI.login({ email, password });
        const { token: newToken, user: userData, verification_required } = response.data;
        
        
        const enhancedUser = enhanceUserData(userData);
        
        try {
            if (newToken) {
                localStorage.setItem('receppet_token', newToken);
                tokenRef.current = newToken;
                setToken(newToken);
            }
            localStorage.setItem('receppet_user', JSON.stringify(enhancedUser));
        } catch (e) {
            console.warn('Could not save to localStorage');
        }
        setUser(enhancedUser);
        
        return { user: enhancedUser, verification_required };
    };

    const register = async (data) => {
        // CRÍTICO: Limpar TODA a sessão anterior antes do registro
        // Limpar localStorage SINCRONAMENTE
        try {
            localStorage.removeItem('receppet_token');
            localStorage.removeItem('receppet_user');
            localStorage.removeItem('receppet_active_panel');
        } catch (e) {
            console.warn('Could not clear localStorage');
        }
        // Limpar tokenRef (evita que loadUser recarregue o usuário antigo)
        tokenRef.current = null;
        setToken(null);
        setUser(null);
        // Limpar cookies de sessão (evita que backend use session_token residual)
        try {
            document.cookie = 'session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            document.cookie = 'session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=' + window.location.hostname;
        } catch (e) {}
        
        const response = await authAPI.register(data);
        const { token: newToken, user: userData, verification_required } = response.data;
        
        const enhancedUser = enhanceUserData(userData);
        
        try {
            if (newToken) {
                localStorage.setItem('receppet_token', newToken);
                tokenRef.current = newToken;
                setToken(newToken);
            }
            localStorage.setItem('receppet_user', JSON.stringify(enhancedUser));
        } catch (e) {
            console.warn('Could not save to localStorage');
        }
        setUser(enhancedUser);
        
        return { user: enhancedUser, verification_required };
    };

    const logout = async () => {
        try {
            await authAPI.logout();
        } catch (e) {
            // Ignore logout errors
        }
        // Limpar COMPLETAMENTE localStorage
        try {
            localStorage.removeItem('receppet_token');
            localStorage.removeItem('receppet_user');
            localStorage.removeItem('receppet_active_panel');
            localStorage.removeItem('receppet_cookie_consent');
        } catch (e) {
            console.warn('Could not clear localStorage');
        }
        // Limpar cookies de sessão
        try {
            document.cookie = 'session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            document.cookie = 'session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=' + window.location.hostname;
        } catch (e) {}
        tokenRef.current = null;
        setToken(null);
        setUser(null);
    };

    const updateUser = (userData) => {
        setUser(userData);
        if (userData) {
            try {
                localStorage.setItem('receppet_user', JSON.stringify(userData));
            } catch (e) {
                console.warn('Could not save to localStorage');
            }
        }
    };

    const loginWithGoogle = (newToken, userData) => {
        const enhancedUser = enhanceUserData(userData);
        if (newToken) {
            try {
                localStorage.setItem('receppet_token', newToken);
            } catch (e) {}
            tokenRef.current = newToken;
            setToken(newToken);
        }
        try {
            localStorage.setItem('receppet_user', JSON.stringify(enhancedUser));
        } catch (e) {}
        setUser(enhancedUser);
        return enhancedUser;
    };

    const updateToken = (newToken) => {
        if (newToken) {
            try {
                localStorage.setItem('receppet_token', newToken);
            } catch (e) {}
            tokenRef.current = newToken;
            setToken(newToken);
        }
    };

    const refreshUser = async () => {
        try {
            const response = await authAPI.getMe();
            const enhancedUser = enhanceUserData(response.data);
            setUser(enhancedUser);
            try {
                localStorage.setItem('receppet_user', JSON.stringify(enhancedUser));
            } catch (e) {
                console.warn('Could not save to localStorage');
            }
            return enhancedUser;
        } catch (error) {
            console.error('Error refreshing user:', error);
            return null;
        }
    };

    return (
        <AuthContext.Provider value={{ 
            user, 
            token, 
            loading, 
            login, 
            register, 
            logout, 
            updateUser,
            refreshUser,
            loginWithGoogle,
            updateToken
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};
